import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowRight, BadgeCheck, CalendarDays, Cloud, FileDown, FolderOpen, LockKeyhole, RefreshCcw, ShieldCheck, Sparkles } from 'lucide-react'
import { CopyProgress } from './components/CopyProgress'
import { CloudVaultCard } from './components/CloudVaultCard'
import { DestinationCard } from './components/DestinationCard'
import { FilterSheet } from './components/FilterSheet'
import { GmailBackup } from './components/GmailBackup'
import { InfoSections } from './components/InfoSections'
import { LaunchOffer } from './components/LaunchOffer'
import { RecipeRail } from './components/RecipeRail'
import { SourceDeck } from './components/SourceDeck'
import { uploadCloudBlob } from './lib/cloud'
import { licenseDurationLabel } from './lib/license'
import { authorizeDrive, downloadDriveItem, DriveTokenExpiredError, isFolder, isValidClientId, listDriveItems, revokeDrive } from './lib/drive'
import { pickDestination, supportsLocalDestination, writeBlob, writeManifest } from './lib/filesystem'
import { categoryFor, fileMatchesRules, formatBytes, formatDate, humanRule, interpretRule, recipes, rulesForRecipe } from './lib/rules'
import type { CloudQuote } from './lib/cloud'
import type { BackupDestinationMode, BackupProfileId, CopyProgressState, CopyRecipe, CopyRecord, CopyRules, CopyStage, DriveItem, PendingBackupPlan } from './types'

const categoryFolders: Record<string, string> = { images: 'Fotos', videos: 'Vídeos', audio: 'Audio', documents: 'Documentos', sheets: 'Hojas de cálculo', presentations: 'Presentaciones', pdf: 'PDF', other: 'Otros' }
const initialProgress: CopyProgressState = { current: 0, total: 0, bytesCopied: 0, bytesTotal: 0, bytesKnown: false, currentName: '', errors: [] }
const demoDriveItems: DriveItem[] = [
  { id: 'demo-photo', name: 'Vacaciones-2021.jpg', mimeType: 'image/jpeg', size: '18500000000', modifiedTime: '2021-08-22T10:00:00Z' },
  { id: 'demo-video', name: 'Familia-2021.mp4', mimeType: 'video/mp4', size: '97600000000', modifiedTime: '2021-12-14T10:00:00Z' },
  { id: 'demo-audio', name: 'Concierto-2021.mp3', mimeType: 'audio/mpeg', size: '2400000000', modifiedTime: '2021-07-11T10:00:00Z' },
  { id: 'demo-photo-2', name: 'Archivo-2020.png', mimeType: 'image/png', size: '7100000000', modifiedTime: '2020-03-02T10:00:00Z' },
]

function safeSegment(value: string) { return value.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '·').trim() || 'sin-nombre' }

function driveRelativePath(file: DriveItem, name: string, allItems: DriveItem[], rules: CopyRules) {
  const modified = file.modifiedTime ? new Date(file.modifiedTime) : new Date()
  const year = String(modified.getFullYear())
  const month = String(modified.getMonth() + 1).padStart(2, '0')
  const category = categoryFolders[categoryFor(file)]
  if (rules.organization === 'year-month-type') return `libregoo/${year}/${month}/${category}/${safeSegment(name)}`
  if (rules.organization === 'year-type') return `libregoo/${year}/${category}/${safeSegment(name)}`

  const folders = new Map(allItems.filter(isFolder).map((item) => [item.id, item]))
  const path: string[] = []
  let parent = file.parents?.[0]
  const visited = new Set<string>()
  while (parent && !visited.has(parent)) {
    visited.add(parent)
    const node = folders.get(parent)
    if (!node) break
    path.unshift(safeSegment(node.name))
    parent = node.parents?.[0]
  }
  return ['libregoo', 'Drive', ...path, safeSegment(name)].join('/')
}

function downloadJson(name: string, value: unknown) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = name
  anchor.click()
  URL.revokeObjectURL(url)
}

function recordIssues(records: CopyRecord[]) {
  return records.flatMap((record) => {
    const issues: string[] = []
    if (record.status === 'failed') issues.push(`${record.sourceName}: ${record.error ?? 'No se pudo copiar localmente.'}`)
    if (record.cloudStatus === 'failed') issues.push(`${record.sourceName}: copia local guardada, pero nube no completada (${record.cloudError ?? 'error desconocido'}).`)
    return issues
  })
}

function rulesFromSavedPlan(plan: PendingBackupPlan) {
  const recipesByProfile: Record<BackupProfileId, CopyRecipe['id']> = { complete: 'all-drive', media: 'media-2y', work: 'work-docs' }
  const result = rulesForRecipe(recipesByProfile[plan.profile])
  result.organization = plan.organization
  if (plan.datePreset === 'custom') {
    result.age = 'custom'
    result.after = plan.after
    result.before = plan.before
  } else {
    result.age = plan.datePreset
    result.after = undefined
    result.before = undefined
  }
  return { rules: result, recipe: recipesByProfile[plan.profile] }
}

function savedPlanDate(plan: PendingBackupPlan) {
  if (plan.datePreset === 'all') return 'cualquier fecha'
  if (plan.datePreset === 'custom') return `${plan.after || 'sin inicio'} — ${plan.before || 'sin fin'}`
  return `anterior a ${plan.datePreset === '6m' ? '6 meses' : plan.datePreset === '1y' ? '1 año' : plan.datePreset === '2y' ? '2 años' : '5 años'}`
}

export default function App() {
  const showWorkspace = new URLSearchParams(window.location.search).get('workspace') === '1'
  const [recipe, setRecipe] = useState<CopyRecipe['id']>('media-2y')
  const [rules, setRules] = useState<CopyRules>(() => rulesForRecipe('media-2y'))
  // El Client ID web es público; nunca se incluye el Client Secret.
  const [clientId, setClientId] = useState(import.meta.env.VITE_GOOGLE_CLIENT_ID || '682296387506-ld4tn30ativ6n0t9e7mt8c149duvqrvs.apps.googleusercontent.com')
  const [token, setToken] = useState('')
  const [items, setItems] = useState<DriveItem[]>([])
  const [destination, setDestination] = useState<FileSystemDirectoryHandle | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [stage, setStage] = useState<CopyStage>('idle')
  const [message, setMessage] = useState('Elige una receta y conéctate a Drive cuando estés listo.')
  const [naturalLanguage, setNaturalLanguage] = useState('')
  const [progress, setProgress] = useState<CopyProgressState>(initialProgress)
  const [records, setRecords] = useState<CopyRecord[]>([])
  const [purchaseState, setPurchaseState] = useState<'checking' | 'active' | 'missing'>('checking')
  const [destinationMode, setDestinationMode] = useState<BackupDestinationMode>('local')
  const [cloudSelected, setCloudSelected] = useState(false)
  const [cloudQuote, setCloudQuote] = useState<CloudQuote | null>(null)
  const [appliedPlan, setAppliedPlan] = useState<PendingBackupPlan | null>(null)
  const copyLock = useRef(false)

  useEffect(() => {
    if (!showWorkspace || window.location.hash !== '#gmail') return
    const frame = window.requestAnimationFrame(() => {
      document.getElementById('gmail')?.scrollIntoView({ behavior: 'auto', block: 'start' })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [showWorkspace])

  useEffect(() => {
    const sessionId = window.localStorage.getItem('libregoo.checkout-session')
    if (!sessionId) { setPurchaseState('missing'); return }
    fetch(`/api/checkout/session/${encodeURIComponent(sessionId)}`)
      .then(async (response) => ({ response, payload: await response.json() as { paid?: boolean } }))
      .then(({ response, payload }) => setPurchaseState(response.ok && payload.paid ? 'active' : 'missing'))
      .catch(() => setPurchaseState('missing'))
  }, [])

  useEffect(() => {
    const storedPlan = window.localStorage.getItem('libregoo.pending-plan')
    if (!storedPlan) return
    try {
      const plan = JSON.parse(storedPlan) as PendingBackupPlan
      if (plan.version !== 1 || !Array.isArray(plan.sources) || !['complete', 'media', 'work'].includes(plan.profile)) return
      const next = rulesFromSavedPlan(plan)
      setRecipe(next.recipe)
      setRules(next.rules)
      setDestinationMode(plan.destinationMode ?? (plan.cloudSelected ? 'both' : 'local'))
      setCloudSelected(plan.cloudSelected)
      setAppliedPlan(plan)
      setMessage(`Configuración recuperada: ${savedPlanDate(plan)}. Conecta Google cuando quieras analizar el alcance real.`)
    } catch {
      window.localStorage.removeItem('libregoo.pending-plan')
    }
  }, [])

  const folders = useMemo(() => items.filter(isFolder).sort((a, b) => a.name.localeCompare(b.name, 'es')), [items])
  const filteredItems = useMemo(() => items.filter((item) => fileMatchesRules(item, rules)), [items, rules])
  const knownBytes = useMemo(() => filteredItems.reduce((total, item) => total + Number(item.size ?? 0), 0), [filteredItems])
  const clientReady = isValidClientId(clientId)
  const localSupported = supportsLocalDestination()
  const requiresLocalDestination = destinationMode === 'local' || destinationMode === 'both'
  const activeRecipe = recipes.find((item) => item.id === recipe)!
  const isCopying = stage === 'copying'
  const hasActivePass = purchaseState === 'active'
  const receiveCloudQuote = useCallback((quote: CloudQuote | null) => setCloudQuote(quote), [])

  const updateRules = (nextRules: CopyRules) => {
    if (!copyLock.current) setRules(nextRules)
  }

  const selectRecipe = (id: CopyRecipe['id']) => {
    if (copyLock.current) return
    setRecipe(id)
    setRules(rulesForRecipe(id))
    setMessage(id === 'custom' ? 'Empieza con una regla vacía y ajusta los filtros a tu gusto.' : 'Receta actualizada. Puedes afinar cualquier detalle en Ajustar esta copia.')
    setStage(items.length ? 'ready' : 'idle')
  }

  const interpretNaturalLanguage = () => {
    if (copyLock.current) return
    if (!naturalLanguage.trim()) return
    setRecipe('custom')
    setRules((current) => interpretRule(naturalLanguage, current))
    setShowFilters(true)
    setMessage('He convertido tu frase en filtros que puedes revisar y cambiar antes de escanear Drive.')
  }

  const connectDrive = async () => {
    if (copyLock.current) return
    try {
      setMessage('Abriendo el consentimiento oficial de Google…')
      const accessToken = await authorizeDrive(clientId)
      setToken(accessToken)
      setMessage('Drive conectado con permiso de sólo lectura. Ahora analiza el alcance de la copia.')
      setStage('idle')
    } catch (error) {
      setStage('error')
      setMessage(error instanceof Error ? error.message : 'No fue posible conectar Drive.')
    }
  }

  const scanDrive = async () => {
    if (copyLock.current) return
    if (!token) { setMessage('Conecta Drive antes de analizar los archivos.'); return }
    try {
      setStage('scanning')
      setMessage('Leyendo el inventario de Drive. Sólo se consultan metadatos para construir la estimación.')
      const driveItems = await listDriveItems(token, rules.includeTrash, (count) => setMessage(`Analizando Drive: ${count.toLocaleString('es-ES')} elementos encontrados…`))
      setItems(driveItems)
      setStage('ready')
      setMessage(`Listo: ${driveItems.length.toLocaleString('es-ES')} elementos analizados. Revisa el alcance antes de escribir la copia.`)
    } catch (error) {
      setStage('error')
      setMessage(error instanceof Error ? error.message : 'No se pudo analizar Drive.')
    }
  }

  const loadDemoInventory = () => {
    if (copyLock.current) return
    setItems(demoDriveItems)
    setStage('ready')
    setMessage('Inventario de demostración cargado: puedes probar filtros, la estimación S3 y la revisión final. No se ha conectado Google ni se subirá ningún archivo.')
  }

  const chooseDestination = async () => {
    if (copyLock.current) return
    try {
      const handle = await pickDestination()
      setDestination(handle)
      setMessage(`Destino elegido: ${handle.name}. No se copiará nada hasta que pulses Iniciar copia.`)
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      setMessage(error instanceof Error ? error.message : 'No fue posible abrir el selector de carpetas.')
    }
  }

  const beginCopy = async () => {
    if (copyLock.current) return
    if (!hasActivePass) { window.location.assign('/copia'); return }
    if (stage === 'scanning') { setMessage('Espera a que termine el análisis antes de iniciar la copia.'); return }
    if (!token) { setMessage('Conecta Drive antes de iniciar la copia.'); return }
    if (requiresLocalDestination && !destination) { setMessage('Elige una carpeta local antes de iniciar la copia.'); return }
    if (!filteredItems.length) { setMessage('No hay archivos que coincidan con esta regla. Ajusta los filtros y vuelve a analizar Drive.'); return }
    const cloudReady = cloudSelected && Boolean(cloudQuote?.uploadsEnabled)
    if (destinationMode === 'cloud' && !cloudReady) { setMessage('La copia sólo en nube necesita que Amazon S3 esté activado y que la cotización cloud esté disponible.'); return }

    const runItems = [...filteredItems]
    const runRules: CopyRules = { ...rules, categories: { ...rules.categories } }
    const runDestination = destination
    const runToken = token
    const runRecipe = activeRecipe
    const runCloud = cloudReady
    const runCloudSession = window.localStorage.getItem('libregoo.checkout-session')
    const copyRecords: CopyRecord[] = []
    const bytesKnown = runItems.every((item) => item.size !== undefined)
    const bytesTotal = runItems.reduce((sum, item) => sum + Number(item.size ?? 0), 0)
    let bytesCopied = 0
    copyLock.current = true
    setProgress({ current: 0, total: runItems.length, bytesCopied: 0, bytesTotal, bytesKnown, currentName: '', errors: [] })
    setStage('copying')
    setMessage(destinationMode === 'cloud'
      ? 'La copia se está enviando directamente desde Google a Amazon S3. Mantén esta pestaña abierta.'
      : cloudSelected && !runCloud
      ? 'La copia se está descargando directamente desde Google a tu carpeta local. La nube queda como estimación hasta activar proveedor y facturación cloud.'
      : runCloud
        ? 'La copia se está descargando directamente desde Google a tu carpeta local y a tu bucket cloud. Mantén esta pestaña abierta.'
        : 'La copia se está descargando directamente desde Google a tu carpeta local. Mantén esta pestaña abierta.')

    const persistManifest = async (status: 'complete' | 'partial' | 'interrupted') => {
      try {
        const metadata = { regla: humanRule(runRules), receta: runRecipe.title, origen: 'Google Drive', destino: runDestination?.name ?? 'Amazon S3', estado: status }
        if (runDestination) {
          await writeManifest(runDestination, copyRecords, metadata)
          return true
        }
        if (runCloud && runCloudSession) {
          const manifest = new Blob([JSON.stringify({ herramienta: 'libregoo Directo PWA', generado: new Date().toISOString(), ...metadata, archivos: copyRecords }, null, 2)], { type: 'application/json' })
          await uploadCloudBlob(runCloudSession, 'libregoo/manifiesto-copia.json', manifest)
          return true
        }
        throw new Error('No hay un destino activo para guardar el manifiesto.')
      } catch (error) {
        const detail = error instanceof Error ? error.message : 'No se pudo escribir el manifiesto local.'
        setProgress((current) => ({ ...current, errors: [...current.errors, detail] }))
        return false
      }
    }

    try {
      for (let index = 0; index < runItems.length; index += 1) {
        const item = runItems[index]
        try {
          setProgress((current) => ({ ...current, currentName: item.name }))
          const downloaded = await downloadDriveItem(runToken, item, runRules.exportFormat)
          const outputPath = driveRelativePath(item, downloaded.name, items, runRules)
          const writtenPath = runDestination ? await writeBlob(runDestination, outputPath, downloaded.blob) : ''
          let cloudStatus: CopyRecord['cloudStatus'] = 'not-requested'
          let cloudKey: string | undefined
          let cloudError: string | undefined
          if (runCloud) {
            try {
              if (!runCloudSession) throw new Error('No se encontró el pase que autoriza la copia cloud en este navegador.')
              cloudKey = await uploadCloudBlob(runCloudSession, outputPath, downloaded.blob)
              cloudStatus = 'copied'
            } catch (error) {
              cloudStatus = 'failed'
              cloudError = error instanceof Error ? error.message : 'No se pudo enviar este archivo a nube.'
            }
          }
          bytesCopied += downloaded.blob.size
          const copied = Boolean(writtenPath || cloudKey)
          copyRecords.push({ id: item.id, sourceName: item.name, outputPath: writtenPath || cloudKey || outputPath, bytes: downloaded.blob.size, status: copied ? 'copied' : 'failed', copiedAt: new Date().toISOString(), error: copied ? undefined : cloudError ?? 'No se pudo guardar el archivo.', cloudStatus, cloudKey, cloudError })
        } catch (error) {
          const detail = error instanceof Error ? error.message : 'Error desconocido.'
          copyRecords.push({ id: item.id, sourceName: item.name, outputPath: '', bytes: 0, status: 'failed', copiedAt: new Date().toISOString(), error: detail })
          if (error instanceof DriveTokenExpiredError) {
            const errors = recordIssues(copyRecords)
            setProgress((current) => ({ ...current, current: index + 1, bytesCopied, errors }))
            const manifestSaved = await persistManifest('interrupted')
            setRecords(copyRecords)
            setStage(manifestSaved ? 'partial' : 'error')
            setMessage(manifestSaved ? 'Google pidió renovar el permiso. Los archivos ya copiados y el manifiesto parcial permanecen en tu carpeta.' : 'Google pidió renovar el permiso y no se pudo guardar el manifiesto. Los archivos ya copiados permanecen en tu carpeta.')
            return
          }
        }
        const errors = recordIssues(copyRecords)
        setProgress((current) => ({ ...current, current: index + 1, bytesCopied, errors }))
      }

      const errors = recordIssues(copyRecords)
      const manifestSaved = await persistManifest(errors.length ? 'partial' : 'complete')
      setRecords(copyRecords)
      if (!manifestSaved) {
        setStage('error')
        setMessage('Los archivos se copiaron, pero no se pudo guardar el manifiesto. Revisa el permiso de la carpeta antes de repetir la copia.')
      } else if (errors.length) {
        setStage('partial')
        setMessage(`La copia terminó con ${errors.length} incidencia(s). El manifiesto local indica exactamente qué archivos o réplicas cloud requieren atención.`)
      } else {
        setStage('complete')
        setMessage('Copia terminada y manifiesto guardado dentro de libregoo en la carpeta que seleccionaste.')
      }
    } finally {
      copyLock.current = false
    }
  }

  const downloadManifest = () => downloadJson('manifiesto-libregoo.json', { generado: new Date().toISOString(), regla: humanRule(rules), archivos: records })

  const disconnect = () => {
    if (copyLock.current) return
    if (token) revokeDrive(token)
    setToken('')
    setItems([])
    setStage('idle')
    setMessage('Se revocó la sesión en este navegador. También puedes retirar el acceso desde tu cuenta Google.')
  }

  return <div className="app-shell">
    <header className="topbar">
      <a className="brand wordmark-logo" href="/" aria-label="libregoo, inicio">libregoo</a>
      <nav><a href="#como-funciona">Cómo funciona</a><a href="#preguntas">Preguntas</a><a href="#precio">Precio</a><a href="/legal.html" target="_blank" rel="noreferrer">Legal</a></nav>
      <span className="beta-pill">CONEXIÓN DIRECTA CON GOOGLE</span>
    </header>

    <main>
      <section className="hero">
        <div className="hero-copy"><p className="eyebrow">TU CUENTA GOOGLE, BAJO CONTROL</p><h1><i>Limpia, exporta</i><br />y haz backup de tus<br />cuentas Google.</h1><p className="hero-text">Conecta tu cuenta, descubre qué ocupa espacio y decide si quieres crear una copia, limpiar archivos ya protegidos o combinar ambas cosas. Nunca borramos por ti.</p><div className="hero-actions"><a className="button button-primary hero-primary-cta" href="/copia"><span>Empezar</span><small>Analiza tu cuenta antes de pagar</small><ArrowRight size={21} /></a><span><BadgeCheck size={16} /> Sin borrar nada · Sólo lectura</span></div></div>
        <div className="hero-proof"><div className="proof-head"><span>RUTA DE TU COPIA</span><span className="live-dot">EN LOCAL</span></div><div className="proof-path"><div className="proof-node google"><Cloud size={20} /><span>Tu cuenta Google</span><small>Gmail · Fotos · Drive</small></div><div className="proof-link" /><div className="proof-node rule"><Sparkles size={19} /><span>Tu regla</span><small>{activeRecipe.title}</small></div><div className="proof-link" /><div className="proof-node disk"><FolderOpen size={20} /><span>Tu carpeta</span><small>{destination?.name ?? 'Aún sin elegir'}</small></div></div><div className="proof-footer"><LockKeyhole size={15} /> Cada fuente pide sólo el acceso que necesita.</div></div>
      </section>

      <div id="sources"><SourceDeck /></div>

      <LaunchOffer />

      {showWorkspace && <>
      <section className="workspace workspace-single" id="drive">
        <div className="builder">
          <section className="builder-heading"><div><p className="eyebrow">GOOGLE DRIVE · REGLA Y ORGANIZACIÓN</p><h2>Documentos, archivos y multimedia de Drive.</h2><p>Empieza con una receta. Después, si quieres, abre todos los filtros.</p></div><div className="source-status"><span className={token ? 'status-dot is-connected' : 'status-dot'} /><strong>{token ? 'Drive conectado' : 'Drive sin conectar'}</strong>{token && <button type="button" onClick={disconnect} disabled={isCopying}>Desconectar</button>}</div></section>
          <RecipeRail selected={recipe} onSelect={selectRecipe} disabled={isCopying} />
          {appliedPlan && <section className="saved-plan-notice"><CalendarDays size={20} /><div><span className="kicker">CONFIGURACIÓN GUARDADA</span><strong>{appliedPlan.sources.join(' · ')} · {savedPlanDate(appliedPlan)}</strong><p>Destino: {destinationMode === 'cloud' ? 'sólo nube en Amazon S3' : destinationMode === 'both' ? 'copia local + Amazon S3' : 'sólo copia local'}. {requiresLocalDestination ? 'La carpeta se autoriza antes de iniciar.' : 'La copia estará disponible online mientras el servicio cloud esté activo.'}</p></div><a href="/copia">Cambiar selección</a></section>}

          <section className="assistant-box"><div className="assistant-icon"><Sparkles size={19} /></div><div><span className="kicker">ASISTENTE LOCAL</span><strong>Dímelo con tus palabras</strong><p>Por ejemplo: “guarda fotos y vídeos de más de 2 años, mayores de 500 MB”.</p></div><div className="assistant-input"><input aria-label="Describe la regla de copia" value={naturalLanguage} disabled={isCopying} onChange={(event) => setNaturalLanguage(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') interpretNaturalLanguage() }} placeholder="Describe tu copia…" /><button type="button" onClick={interpretNaturalLanguage} disabled={isCopying}>Interpretar</button></div></section>
          <FilterSheet open={showFilters} onOpen={() => { if (!isCopying) setShowFilters((value) => !value) }} rules={rules} folders={folders} onChange={updateRules} disabled={isCopying} />

          <section className="rule-preview"><div><span className="kicker">ASÍ QUEDARÁ TU COPIA</span><strong>{humanRule(rules)}</strong></div><span>{rules.organization === 'original' ? 'Estructura original' : rules.organization === 'year-month-type' ? 'Año / mes / tipo' : 'Año / tipo'}</span></section>

          {!clientReady && <section className="configuration-card"><div><span className="kicker">PREPARACIÓN NECESARIA</span><h3>Conecta el Client ID oficial de Google</h3><p>Para abrir el consentimiento de Drive, la PWA necesita un Client ID web registrado para este dominio. Es un identificador público, no una clave secreta y no se guarda en nuestros servidores.</p></div><div className="client-id-row"><input value={clientId} disabled={isCopying} onChange={(event) => setClientId(event.target.value)} placeholder="1234567890-…apps.googleusercontent.com" aria-label="Client ID OAuth de Google" /><a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer">Abrir Google Cloud</a></div></section>}
          {!clientReady && <section className="demo-card"><div><span className="kicker">MODO DEMOSTRACIÓN</span><strong>Prueba el recorrido sin conectar terceros.</strong><p>Carga un inventario ficticio para probar filtros, cálculo cloud y revisión final. No se conecta Google ni S3.</p></div><button className="button button-secondary" type="button" onClick={loadDemoInventory} disabled={isCopying}>Cargar ejemplo</button></section>}

          <section className="connect-card"><div><span className="kicker">ORIGEN</span><h3>Conecta Drive sólo cuando quieras analizarlo</h3><p>Pediremos acceso de <strong>solo lectura</strong> y Google mostrará exactamente el permiso antes de concederlo.</p></div><button className="button button-primary" type="button" onClick={connectDrive} disabled={!clientReady || stage === 'scanning' || isCopying}><Cloud size={18} /> {token ? 'Conectado a Drive' : 'Conectar Drive'}</button></section>
          {requiresLocalDestination && <DestinationCard name={destination?.name} supported={localSupported} onPick={chooseDestination} disabled={isCopying} />}
          {destinationMode === 'cloud' && <section className="destination-cloud-notice"><Cloud size={22} /><div><span className="kicker">DESTINO CLOUD PRINCIPAL</span><h3>Tu copia estará disponible online.</h3><p>Los archivos se guardarán en Amazon S3 y quedarán respaldados por la infraestructura cloud mientras el servicio de nube esté activo. Verás la cotización y el estado del proveedor antes de comenzar.</p></div></section>}

          <section className="review-card"><div className="review-top"><div><span className="kicker">REVISIÓN</span><h3>Primero mira el alcance. Después, copia.</h3><p aria-live="polite">{message}</p></div><button className="button button-secondary" type="button" onClick={scanDrive} disabled={!token || stage === 'scanning' || isCopying}>{stage === 'scanning' ? <RefreshCcw className="spin" size={17} /> : <FileDown size={17} />}{stage === 'scanning' ? 'Analizando…' : items.length ? 'Actualizar estimación' : 'Analizar mi Drive'}</button></div>
            {items.length > 0 && <div className="estimate-grid"><div><strong>{filteredItems.length.toLocaleString('es-ES')}</strong><span>archivos que cumplen la regla</span></div><div><strong>{formatBytes(knownBytes)}</strong><span>tamaño conocido en Drive</span></div><div><strong>{folders.length.toLocaleString('es-ES')}</strong><span>carpetas detectadas</span></div><div><strong>{filteredItems.filter((item) => !item.size).length.toLocaleString('es-ES')}</strong><span>documentos con tamaño por confirmar</span></div></div>}
            {items.length > 0 && <CloudVaultCard totalBytes={knownBytes} objectCount={filteredItems.length} selected={cloudSelected} destinationMode={destinationMode} onSelected={setCloudSelected} onQuote={receiveCloudQuote} />}
            {items.length > 0 && <div className="review-bottom"><span><ShieldCheck size={16} /> {hasActivePass ? `Licencia de ${licenseDurationLabel} activa. ${destinationMode === 'cloud' ? 'La copia irá a Amazon S3.' : 'Nada se escribe hasta confirmar el inicio.'}` : `Analiza gratis. La licencia de ${licenseDurationLabel} se pide sólo antes de ejecutar.`}</span><button className="button button-primary" type="button" onClick={beginCopy} disabled={(requiresLocalDestination && !destination) || (destinationMode === 'cloud' && !cloudQuote?.uploadsEnabled) || !filteredItems.length || isCopying || stage === 'scanning' || purchaseState === 'checking'}>{hasActivePass ? destinationMode === 'cloud' ? 'Iniciar copia en la nube' : destinationMode === 'both' ? 'Iniciar copia local + nube' : 'Iniciar copia local' : 'Desbloquear esta copia — 4,90 €'} <ArrowRight size={17} /></button></div>}
          </section>
          <CopyProgress stage={stage} progress={progress} onDownloadManifest={downloadManifest} />
        </div>
      </section>

      <GmailBackup clientId={clientId} clientReady={clientReady} destination={destination} hasActivePass={hasActivePass} initialQuery={appliedPlan?.gmailQuery} />

      <section className="future-sources" id="fotos"><div><p className="eyebrow">GOOGLE FOTOS · BIBLIOTECA COMPLETA</p><h2>Tus fotos y vídeos también cuentan.</h2><p>Google ya no permite a una app descargar automáticamente toda tu biblioteca existente. Para una copia completa, libregoo preparará una importación local guiada del archivo oficial; para selecciones concretas, usará el Picker de Google Fotos.</p></div><div className="future-grid"><article><span>◌</span><h3>Biblioteca completa</h3><p>Importación local de archivos oficiales, organizada por fecha y tipo, sin enviar multimedia a un servidor.</p><em>IMPORTADOR LOCAL · SIGUIENTE INCREMENTO</em></article><article><span>✓</span><h3>Selección puntual</h3><p>Elige álbumes o elementos concretos mediante el Picker oficial de Google Fotos.</p><em>PICKER OFICIAL</em></article><article><span>↻</span><h3>Aplicación local</h3><p>Reanudación, ZIP múltiples, hashes, copias programadas y NAS para bibliotecas grandes.</p><em>FASE DE ESCRITORIO</em></article></div></section>
      </>}
      {!showWorkspace && <InfoSections />}
    </main>

    <footer><span>© 2026 libregoo · Sistemas Web Cinetic S.L.</span><span>Tu copia. Tu carpeta. Tu control.</span><span className="footer-links"><a href="/legal.html" target="_blank" rel="noreferrer">Aviso legal</a><a href="/privacidad.html" target="_blank" rel="noreferrer">Privacidad</a><a href="/condiciones.html" target="_blank" rel="noreferrer">Condiciones</a></span></footer>
  </div>
}
